import { MenuItem, Divider } from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import ParentAbsences from '../../component/ChildOwnerComponents/ParentAbsences/ParentAbsences';
import ParentFoodMenu from '../../component/ChildOwnerComponents/ParentFoodMenu/ParentFoodMenu';
import SingleMessage from '../../component/ChildOwnerComponents/ParentMailBox/ParentMessage/SingleMessage/SingleMessage';
import ParentMainPage from '../../component/ChildOwnerComponents/ParentMain/ParentMain';
import ParentMessages from '../../component/ChildOwnerComponents/ParentMailBox/ParentMailBox';
import ParentMealPlan from '../../component/ChildOwnerComponents/ParentMealPlan/ParentMealPlan';
import ParentOrder from '../../component/ChildOwnerComponents/ParentOrder/ParentOrder';
import ParentOrderHistory from '../../component/ChildOwnerComponents/ParentOrderHistory/ParentOrderHistory';
import ParentOrders from '../../component/ChildOwnerComponents/ParentOrders/ParentOrders';
import ParentSettings from '../../component/ChildOwnerComponents/ParentSettings/ParentSettings';
import ParentHeader from '../../component/Headers/ParentHeader/ParentHeader';
import IconComponent from '../../component/IconComponent/IconComponent';
import { ParentBasketContextProvider } from '../../context/ParentBasketProvider';
import { ParentOrderContextProvider } from '../../context/ParentOrderContext';
import { useSideNavContext } from '../../context/SideNavProvider';
import useAuth from '../../hooks/useAuth';
import { SideMenuOptionInterface } from '../../shared';
import { ParentPayOffTheBalance } from '../../component/ChildOwnerComponents/PayOffTheBalance/ParentPayOffTheBalance';
import { LanguagesMenuSelector } from '../../component/LanguagesSelectors/LanguagesMenuSelector';
import Tutorials from '../../component/ChildOwnerComponents/Tutorials/Tutorials';
import { Instruction } from '../../shared/type/instruction.interface';
import { Contact } from '../../component/ChildOwnerComponents/Contact/Contact';
import { EditTeacherOrEmployeeProvider } from '../../context/EditTeacherOrEmployeeProvider';
import SingleParentNotification from '../../component/ChildOwnerComponents/ParentMailBox/ParentNotification/SingleNotification/SingleNotification';
import { useSafeTranslation } from '../../hooks/useSafeTranslation';
import { ParentChildren } from '../../component/ChildOwnerComponents/ParentChildren';
import { useParentDashboardContextContext } from '../../context/ParentDashboardContext';
import { useUnreadThreadCountContext } from '../../context/UnreadMessageContext';

const ChildOwner = () => {
  const { t, i18n } = useSafeTranslation();
  const { setNavOptions, toggleShowNav, showNav, setToggleMode } = useSideNavContext();
  const { parentDashboardContext } = useParentDashboardContextContext();
  const { setUnreadThreadCount, unreadThreadCount } = useUnreadThreadCountContext();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const menuOptions: SideMenuOptionInterface[] = useMemo(
    () => [
      {
        header: t('child_owner_dashboard_element.home_page'),
        icon: 'happyHomeIcon',
        link: 'main'
      },
      {
        header: t('child_owner_dashboard_element.absences'),
        icon: 'calendar',
        link: 'absences'
      },
      {
        header: t('child_owner_dashboard_element.meal_plan'),
        icon: 'cutlery',
        link: 'meal-plan'
      },
      {
        header: t('child_owner_dashboard_element.assigned_accounts'),
        icon: 'users',
        link: 'parent-children/assigned-accounts'
      },
      {
        header: t('child_owner_dashboard_element.order_history'),
        icon: 'history',
        link: 'order-history'
      },
      {
        header: (
          <>
            {t('child_owner_dashboard_element.messages')}{' '}
            {unreadThreadCount > 0 && (
              <span className="ml-2 mb-1.5 text-xs bg-brand-600 text-white rounded-full px-1.5 py-0.5">
                {unreadThreadCount}
              </span>
            )}
          </>
        ),
        icon: 'message',
        link: 'mail-box/messages'
      },
      {
        header: t('child_owner_dashboard_element.settings'),
        icon: 'settings',
        link: 'settings/account-settings'
      },
      {
        header: t('child_owner_dashboard_element.tutorials'),
        icon: 'question',
        link: 'instructions'
      },
      {
        header: t('child_owner_dashboard_element.contact'),
        icon: 'phone',
        link: 'contact'
      }
    ],
    [i18n.language, unreadThreadCount]
  );

  const menuList = (
    <>
      <MenuItem
        icon={
          <div className="Header__iconContainer">
            <IconComponent iconName="settings" className="w-full h-full" />
          </div>
        }
        onClick={function () {
          navigate('/parent/settings');
        }}>
        {t('child_owner_dashboard_element.settings')}
      </MenuItem>
      <MenuItem>
        <LanguagesMenuSelector />
      </MenuItem>
      <Divider />
      <MenuItem
        icon={
          <div className="Header__iconContainer">
            <IconComponent iconName="logOutDoor" className="w-full h-full" />
          </div>
        }
        onClick={async function () {
          logout();
        }}>
        {t('child_owner_dashboard_element.logout')}
      </MenuItem>
    </>
  );

  const instructions: Instruction[] = [
    {
      url: 'https://www.youtube.com/watch?v=AcTfxJkt41c',
      title: 'Dodawanie ucznia',
      description: 'Instrukcja jak zgłaszać ucznia jako konsumenta posiłków'
    },
    {
      url: 'https://www.youtube.com/watch?v=WxMMHpWbnzw',
      title: 'Dodawanie breloka/numeru karty',
      description: 'Instrukcja jak dodać informacje o numerze identyfikatora do ucznia/konsumenta'
    }
  ];

  useEffect(() => {
    setNavOptions(menuOptions);
  }, [menuOptions]);

  useEffect(() => {
    setToggleMode('minimal');
    toggleShowNav(true);
  }, []);

  useEffect(() => {
    if (
      parentDashboardContext?.tenants?.[0]?.unreadThreads != null ||
      parentDashboardContext?.tenants[0].unreadAnnouncements != null
    ) {
      setUnreadThreadCount(
        parentDashboardContext.tenants[0].unreadThreads +
          parentDashboardContext?.tenants[0].unreadAnnouncements
      );
    }
  }, [parentDashboardContext?.tenants?.[0]?.unreadThreads, parentDashboardContext?.tenants[0].unreadAnnouncements]);

  return (
    <ParentBasketContextProvider>
      <ParentOrderContextProvider>
        <EditTeacherOrEmployeeProvider withForm={false}>
          <section className="TenantAdminDashboard flex flex-row w-full h-screen">
            <div className="TenantAdminDashboard__contextContainer flex flex-col w-full h-screen">
              <div className="ParentDashboard__headerContainer border-b border-brand-600">
                <ParentHeader notificationList={null} dropdownMenuList={menuList} /> {showNav}
              </div>
              <div
                className={`ParentDashboard__greetingsContainer flex-grow min-h-0 h-screen overflow-y-auto overflow-x-hidden`}>
                <div className="ParentDashboard__contextContainer">
                  <Routes>
                    {/* tpay routes */}
                    <Route path="/main/tpay/success" element={<ParentMainPage />} />
                    <Route path="/main/tpay/error" element={<ParentMainPage />} />
                    {/* other routes */}
                    <Route path="/main/*" element={<ParentMainPage />} />
                    <Route path="/settings/*" element={<ParentSettings />} />
                    <Route path="/order/*" element={<ParentOrder />} />
                    <Route path="/orders/*" element={<ParentOrders />} />
                    <Route path="/food-menu/*" element={<ParentFoodMenu />} />
                    <Route path="/parent-children/*" element={<ParentChildren />} />
                    <Route path="/absences/*" element={<ParentAbsences />} />
                    <Route path="/mail-box/messages/message/:id/*" element={<SingleMessage />} />
                    <Route
                      path="/mail-box/announcements/:id"
                      element={<SingleParentNotification />}
                    />
                    <Route path="/mail-box/*" element={<ParentMessages />} />
                    <Route path="/order-history/*" element={<ParentOrderHistory />} />
                    <Route path="/meal-plan" element={<ParentMealPlan />} />
                    <Route path="/payoff-balance" element={<ParentPayOffTheBalance />} />
                    <Route
                      path="/instructions"
                      element={<Tutorials instructions={instructions} />}
                    />
                    <Route path="/contact" element={<Contact />} />
                  </Routes>
                </div>
              </div>
            </div>
          </section>
        </EditTeacherOrEmployeeProvider>
      </ParentOrderContextProvider>
    </ParentBasketContextProvider>
  );
};

export default ChildOwner;
